import { NavLink, Outlet } from "react-router-dom";
import { useMemo, type ComponentType, type ReactNode } from "react";
import { Home, Download, Wrench, SlidersHorizontal, User, CreditCard, LogOut, ExternalLink } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useAuth } from "@/core/auth/auth";
import { LiveInsightIcon, LiveSpeakIcon, RadiologyLiveIcon } from "@/components/icons/ProductIcons";

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
}

const NAV_ITEMS: NavItem[] = [
  { to: "/portal/home", label: "Home", icon: Home },
  { to: "/portal/downloads", label: "Downloads", icon: Download },
  { to: "/portal/templates", label: "Template Builder", icon: Wrench },
  { to: "/portal/report-preferences", label: "Report Preferences", icon: SlidersHorizontal },
  { to: "/portal/account", label: "Account", icon: User },
  { to: "/portal/billing", label: "Billing", icon: CreditCard },
];

function ProductBanner({
  title,
  description,
  href,
  icon,
}: {
  title: string;
  description: string;
  href: string;
  icon: ReactNode;
}) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="group rounded-xl border border-border/40 bg-card/50 p-4 sm:p-5 transition-all hover:border-primary/50 hover:bg-accent/20"
    >
      <div className="flex items-center gap-4">
        <div className="h-14 w-14 rounded-xl border border-border/50 bg-background/60 shrink-0 flex items-center justify-center">
          {icon}
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h2 className="text-base font-semibold">{title}</h2>
            <ExternalLink className="h-3.5 w-3.5 text-muted-foreground group-hover:text-primary" />
          </div>
          <p className="text-sm text-muted-foreground">{description}</p>
        </div>
      </div>
      <div className="mt-3 text-xs text-primary font-medium">Open Web App</div>
    </a>
  );
}

export function PortalShell() {
  const { user, signOut } = useAuth();

  const initials = useMemo(() => {
    const email = user?.email ?? "U";
    return email.slice(0, 2).toUpperCase();
  }, [user?.email]);

  return (
    <div className="min-h-screen bg-background">
      <div className="mx-auto max-w-7xl px-4 py-6 lg:py-8">
        <header className="mb-5 flex items-center justify-between rounded-xl border border-border/40 bg-card/50 p-3 sm:p-4">
          <div className="flex items-center gap-3">
            <RadiologyLiveIcon size={26} className="h-10 w-auto" />
            <div>
              <div className="text-sm font-semibold">RadiologyLive.ai Portal</div>
              <div className="text-xs text-muted-foreground">User workspace and configuration</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hidden sm:block text-xs text-muted-foreground">{user?.email}</div>
            <Avatar className="h-8 w-8">
              <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">{initials}</AvatarFallback>
            </Avatar>
            <Button size="sm" variant="ghost" onClick={signOut}>
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </header>

        <div className="sticky top-4 z-40 mb-4">
          <section className="grid grid-cols-1 gap-3 lg:grid-cols-2">
            <ProductBanner
              title="RadiologyLive.ai Speak"
              description="Launch deterministic report compiler in web app."
              href="https://speak.radiologylive.ai"
              icon={<LiveSpeakIcon size={34} />}
            />
            <ProductBanner
              title="RadiologyLive.ai Insight"
              description="Open clinical copilot and guidance workflows."
              href="https://insight.radiologylive.ai"
              icon={<LiveInsightIcon size={34} />}
            />
          </section>
        </div>

        <div className="grid grid-cols-1 gap-4 lg:grid-cols-[260px_1fr]">
          <aside className="rounded-xl border border-border/40 bg-card/50 p-3 h-fit lg:sticky lg:top-6">
            <nav className="space-y-1">
              {NAV_ITEMS.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    className={({ isActive }) =>
                      `flex items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors ${
                        isActive
                          ? "bg-primary/10 text-primary font-medium"
                          : "text-muted-foreground hover:text-foreground hover:bg-accent"
                      }`
                    }
                  >
                    <Icon className="h-4 w-4" />
                    {item.label}
                  </NavLink>
                );
              })}
            </nav>
          </aside>

          <main className="rounded-xl border border-border/40 bg-card/50 p-4 sm:p-5">
            <Outlet />
          </main>
        </div>
      </div>
    </div>
  );
}
